import React, { useEffect, useState, useRef } from 'react';
import { Building2, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

interface GoogleBusinessProfile {
  id: string;
  name: string;
  type: 'account' | 'location';
  fullName: string;
  accountName?: string;
  phone?: string;
  address?: any;
}

interface GoogleBusinessCallbackProps {
  onComplete: () => void;
}

export function GoogleBusinessCallback({ onComplete }: GoogleBusinessCallbackProps) {
  const [status, setStatus] = useState<'processing' | 'selecting' | 'saving' | 'success' | 'error'>('processing');
  const [error, setError] = useState<string>('');
  const [profiles, setProfiles] = useState<GoogleBusinessProfile[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string>('');
  const [refreshToken, setRefreshToken] = useState<string>('');
  const [expiresIn, setExpiresIn] = useState<number>(3600);

  // CRITICAL FIX: Prevent double execution in React Strict Mode
  const calledRef = useRef(false);

  useEffect(() => {
    // Guard against double execution
    if (calledRef.current) return;
    calledRef.current = true;

    handleGoogleBusinessCallback();
  }, []);

  const handleGoogleBusinessCallback = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const error = params.get('error');

      if (error) {
        throw new Error(`Google authorization failed: ${error}`);
      }

      if (!code) {
        throw new Error('No authorization code received from Google');
      }

      window.history.replaceState({}, document.title, window.location.pathname);

      setStatus('processing');

      // Get current user ID for custom credentials
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id;

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const redirectUri = `${supabaseUrl}/functions/v1/google-business-oauth`;

      // IMPORTANT: OAuth authorization codes can only be used ONCE
      // We cannot retry with the same code - if we get 429, user must reconnect
      const response = await fetch(`${supabaseUrl}/functions/v1/google-business-oauth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          code,
          redirectUri,
          userId,
        }),
      });

      if (!response || !response.ok) {
        let errorMessage = 'Failed to exchange authorization code';

        try {
          const errorData = await response!.json();

          if (errorData.error) {
            // Handle invalid_grant (code already used or expired)
            if (errorData.error === 'invalid_grant' || errorData.error.includes('invalid_grant')) {
              errorMessage = '⚠️ Authorization code expired or already used.\n\nThis can happen if:\n1. You clicked "Connect" multiple times\n2. The page was refreshed during connection\n3. The authorization took too long\n\nPlease try connecting again.';
            }
            // Enhanced 429 handling with billing info
            else if (response!.status === 429) {
              if (errorData.error.includes('quota') || errorData.error.includes('RATE_LIMIT_EXCEEDED')) {
                errorMessage = '⚠️ Google API quota exceeded.\n\nThis usually means:\n1. Billing is not enabled in Google Cloud Console\n2. The required APIs are not enabled\n3. You need to enable billing and activate: My Business Account Management API\n\nPlease check your Google Cloud Console, wait 2-3 minutes, then try connecting again.';
              } else {
                errorMessage = '⚠️ Too many requests to Google.\n\nPlease wait 2-3 minutes and try connecting again.';
              }
            }
            // Other errors
            else {
              errorMessage = errorData.error;
            }

            if (errorData.details) {
              console.error('Error details:', errorData.details);
            }
          }
        } catch (e) {
          console.error('Failed to parse error response:', e);
          if (response!.status === 429) {
            errorMessage = 'Rate limit exceeded. Please wait 2-3 minutes and try connecting again.';
          } else if (response!.status === 400) {
            errorMessage = 'Authorization failed. Please try connecting again.';
          }
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to authenticate with Google');
      }

      setAccessToken(data.access_token);
      setRefreshToken(data.refresh_token || '');
      setExpiresIn(data.expires_in || 3600);

      const profileList: GoogleBusinessProfile[] = data.profiles || [];

      if (profileList.length === 0) {
        throw new Error('No Google Business profiles found. Please make sure you have access to at least one business location.');
      }

      setProfiles(profileList);
      setStatus('selecting');

      if (profileList.length === 1) {
        setSelectedProfileId(profileList[0].id);
        await saveSelectedProfile(profileList[0], data.access_token, data.refresh_token || '', data.expires_in || 3600);
      }
    } catch (err) {
      console.error('Google Business callback error:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setStatus('error');
      toast.error(err instanceof Error ? err.message : 'Failed to connect Google Business');
    }
  };

  const saveSelectedProfile = async (profile: GoogleBusinessProfile, token: string, refresh: string, tokenExpiresIn: number = 3600) => {
    try {
      setStatus('saving');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const tokenExpiresAt = new Date(Date.now() + tokenExpiresIn * 1000).toISOString();

      const { error: dbError } = await supabase
        .from('connected_accounts')
        .insert({
          id: profile.fullName,
          name: profile.name,
          platform: 'google_business',
          account_type: profile.type,
          user_id: user.id,
          access_token: token,
          refresh_token: refresh || null,
          token_expires_at: tokenExpiresAt,
        });

      if (dbError) {
        if (dbError.code === '23505') {
          setStatus('success');
          toast.success('This Google Business profile is already connected!');
          setTimeout(() => {
            const redirectUrl = localStorage.getItem('google_business_auth_redirect') || '/';
            localStorage.removeItem('google_business_auth_redirect');
            window.location.href = redirectUrl;
          }, 1500);
          return;
        }
        throw dbError;
      }

      setStatus('success');
      toast.success(`Google Business ${profile.type === 'location' ? 'location' : 'account'} connected successfully!`);

      setTimeout(() => {
        const redirectUrl = localStorage.getItem('google_business_auth_redirect') || '/';
        localStorage.removeItem('google_business_auth_redirect');
        window.location.href = redirectUrl;
      }, 2000);
    } catch (err) {
      console.error('Error saving profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to save Google Business profile');
      setStatus('error');
      toast.error(err instanceof Error ? err.message : 'Failed to save profile');
    }
  };

  const handleProfileSelection = async () => {
    if (!selectedProfileId) {
      toast.error('Please select a business profile');
      return;
    }

    const selectedProfile = profiles.find(p => p.id === selectedProfileId);
    if (!selectedProfile) {
      toast.error('Selected profile not found');
      return;
    }

    await saveSelectedProfile(selectedProfile, accessToken, refreshToken, expiresIn);
  };

  const formatAddress = (address: any) => {
    if (!address) return '';
    const parts = [
      address.addressLines?.join(', '),
      address.locality,
      address.administrativeArea,
      address.postalCode,
    ].filter(Boolean);
    return parts.join(', ');
  };

  if (status === 'processing') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <RefreshCw className="w-16 h-16 text-red-600 mx-auto mb-4 animate-spin" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Connecting Google Business</h2>
          <p className="text-gray-600">Please wait while we fetch your business profiles...</p>
        </div>
      </div>
    );
  }

  if (status === 'selecting') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl w-full">
          <div className="flex items-center justify-center mb-6">
            <Building2 className="w-12 h-12 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
            Select Business Profile
          </h2>
          <p className="text-gray-600 mb-6 text-center">
            Choose which Google Business profile you want to connect for posting
          </p>

          <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
            {profiles.map((profile) => (
              <label
                key={profile.id}
                className={`flex items-start space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  selectedProfileId === profile.id
                    ? 'border-red-600 bg-red-50'
                    : 'border-gray-200 hover:border-red-300'
                }`}
              >
                <input
                  type="radio"
                  checked={selectedProfileId === profile.id}
                  onChange={() => setSelectedProfileId(profile.id)}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 mt-1"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900">{profile.name}</div>
                  {profile.accountName && (
                    <div className="text-xs text-gray-500 mt-1">Account: {profile.accountName}</div>
                  )}
                  {profile.phone && (
                    <div className="text-xs text-gray-500 mt-1">Phone: {profile.phone}</div>
                  )}
                  {profile.address && (
                    <div className="text-xs text-gray-500 mt-1">{formatAddress(profile.address)}</div>
                  )}
                  <div className="text-xs text-gray-400 mt-1">
                    {profile.type === 'location' ? 'Business Location' : 'Business Account'}
                  </div>
                </div>
              </label>
            ))}
          </div>

          <button
            onClick={handleProfileSelection}
            disabled={!selectedProfileId}
            className="w-full px-4 py-3 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Connect Selected Profile
          </button>
        </div>
      </div>
    );
  }

  if (status === 'saving') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <RefreshCw className="w-16 h-16 text-red-600 mx-auto mb-4 animate-spin" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Saving Connection</h2>
          <p className="text-gray-600">Completing the setup...</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Successfully Connected!</h2>
          <p className="text-gray-600">Your Google Business profile has been connected. Redirecting...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Connection Failed</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => {
              const redirectUrl = localStorage.getItem('google_business_auth_redirect') || '/';
              localStorage.removeItem('google_business_auth_redirect');
              window.location.href = redirectUrl;
            }}
            className="px-6 py-3 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
          >
            Return to App
          </button>
        </div>
      </div>
    );
  }

  return null;
}
